import React, { useCallback, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import {
  getFlowFactor,
  getWizardData,
  useWizardService,
  WIZARD_EVENTS,
} from '@adsui/wizard';
import { TileHeader, CreationMethodSubTitle } from '@bingads-webui-campaign-react/goals-and-website-tile';
import { CampaignCreationMethodSelection } from '@bingads-webui-campaign-react/campaign-creation-method-selection';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { LanguageCountryStepShimmer } from '@bingads-webui-campaign-react/pmax-lite-shimmers';

export const CampaignCreationMethodStep = () => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { CurrentAccount, Identity } = serverContext;
  const { send, state } = useWizardService();
  const { loadWizardDirectly } = getWizardData(state);
  const isGoalBasedSignup = getFlowFactor(state, 'isGoalBasedSignup');
  const firstName = Identity && Identity.FirstName;

  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const isSmallSize = displayMode === 's' || displayMode === 'm';

  const goNextWthAccountCreated = useCallback((flowFactors) => {
    if (CurrentAccount) {
      send(WIZARD_EVENTS.NEXT, { flowFactors });
    } else {
      send(WIZARD_EVENTS.SAVE_AND_NEXT, { flowFactors });
    }
  }, [CurrentAccount, send]);

  const onClickCreateNewCampaign = useCallback(() => {
    const flowFactors = {
      isGoogleImport: false,
      isFacebookImport: false,
    };

    goNextWthAccountCreated(flowFactors);
  }, [goNextWthAccountCreated]);

  const onClickGoogleImport = useCallback(() => {
    const flowFactors = {
      isGoogleImport: true,
      isFacebookImport: false,
    };
    goNextWthAccountCreated(flowFactors);
  }, [goNextWthAccountCreated]);

  const onClickFacebookImport = useCallback(() => {
    const flowFactors = {
      isGoogleImport: false,
      isFacebookImport: true,
    };
    goNextWthAccountCreated(flowFactors);
  }, [goNextWthAccountCreated]);

  // with cache flow, we need to show the language step first.
  // but before navigating to the language step, the campaign creation step will show first.
  // we need to show LanguageCountryStepShimmer first.
  if (loadWizardDirectly) {
    return <LanguageCountryStepShimmer />;
  }

  return (
    <div className={isGoalBasedSignup ? 'goal-based-creation-method' : 'non-goal-based-creation-method'}>
      <TileHeader
        title={i18n.getString(_TL_('Hi {{firstName}}, how can we help you get started?'), { firstName })}
        subtitle={<CreationMethodSubTitle
          i18n={i18n}
          subTitle={_TL_('You can create a new account and campaign, or you can import existing campaigns from Google Ads or Meta Ads.')}
        />}
        displayMode={displayMode}
      />
      <CampaignCreationMethodSelection
        i18n={i18n}
        onClickCreateNewCampaign={onClickCreateNewCampaign}
        onClickGoogleImport={onClickGoogleImport}
        onClickFacebookImport={onClickFacebookImport}
        isVertical={isSmallSize}
        disableImports={false} // TODO: we need another signal to decide if user is using mobile for mobile integration
        isRebranding
      />
    </div>
  );
};
